<template>
    <div
        :class="[
            'table-responsive-sm',
            {
                'force-mobile': forceMobileView,
                'force-desktop': !forceMobileView && forceDesktopView,
            },
        ]"
    >
        <!-- Mobile View -->
        <div :class="mobileDisplayClass">
            <div class="g-3 row mt-0 mobile-table-container">
                <div
                    class="col-12 col-sm-6 m-0"
                    v-for="record in records"
                    :key="record[idField || 'id']"
                >
                    <div class="mobile-card">
                        <div class="mobile-header" v-if="showHeaderMobile">
                            <!-- Render fields marked with mobileHeader: true -->
                            <template
                                v-for="field in mobileHeaderFields"
                                :key="field.key"
                            >
                                <slot
                                    :name="`cell-${field.key}`"
                                    :record="record"
                                >
                                    <!-- Fallback to default rendering if no slot -->
                                    <span
                                        v-if="
                                            field.type === 'icon' &&
                                            record[field.key]
                                        "
                                        class="icon-style"
                                    >
                                        <i
                                            class="fi"
                                            :class="
                                                record[field.key]?.class ||
                                                record[field.key]
                                            "
                                        ></i>
                                    </span>
                                    <span
                                        v-else-if="field.type === 'user-avatar'"
                                    >
                                        <UserAvatar
                                            :key="record.profile_image_id"
                                            :user="record"
                                            class="avatar-style"
                                            size="2.25rem"
                                            :clickable="
                                                isFieldClickable(field, record)
                                            "
                                        />
                                    </span>
                                    <span
                                        v-else-if="field.type === 'name'"
                                        class="mobile-name"
                                        :class="{
                                            'clickable-content':
                                                isFieldClickable(field, record),
                                        }"
                                        @click="
                                            clickableContentClick(field, record)
                                        "
                                    >
                                        {{
                                            record?.name ||
                                            record[field.key] ||
                                            '—'
                                        }}
                                    </span>
                                    <span v-else-if="field.type === 'starred'">
                                        <ToolTip v-if="isStarDisabled(record)">
                                            <template #tooltip-trigger>
                                                <div class="star-disabled">
                                                    <img
                                                        v-if="record[field.key]"
                                                        src="@/assets/images/icons/filled-star.svg"
                                                        alt="Filled star"
                                                        class="star-icon star-not-allowed"
                                                    />
                                                    <img
                                                        v-else
                                                        src="@/assets/images/icons/star.svg"
                                                        alt="Empty star"
                                                        class="star-icon star-not-allowed"
                                                    />
                                                </div>
                                            </template>
                                            <template
                                                #tooltip-content
                                                v-if="getStarTooltip(record)"
                                            >
                                                {{ getStarTooltip(record) }}
                                            </template>
                                        </ToolTip>
                                        <template v-else>
                                            <img
                                                v-if="record[field.key]"
                                                src="@/assets/images/icons/filled-star.svg"
                                                alt="Filled star"
                                                class="star-icon star-clickable"
                                                @click="
                                                    $emit('star-click', record)
                                                "
                                            />
                                            <img
                                                v-else
                                                src="@/assets/images/icons/star.svg"
                                                alt="Empty star"
                                                class="star-icon star-clickable"
                                                @click="
                                                    $emit('star-click', record)
                                                "
                                            />
                                        </template>
                                    </span>
                                    <span
                                        v-else
                                        class="mobile-name"
                                        :class="{
                                            'clickable-content':
                                                isFieldClickable(field, record),
                                        }"
                                        @click="
                                            clickableContentClick(field, record)
                                        "
                                    >
                                        {{ record[field.key] || '—' }}
                                    </span>
                                </slot>
                            </template>
                            <!-- Default header content (if no mobileHeader fields) -->
                            <template v-if="mobileHeaderFields.length === 0">
                                <!-- Starred -->
                                <span v-if="starredField">
                                    <ToolTip v-if="isStarDisabled(record)">
                                        <template #tooltip-trigger>
                                            <div class="star-disabled">
                                                <img
                                                    v-if="
                                                        record[starredField.key]
                                                    "
                                                    src="@/assets/images/icons/filled-star.svg"
                                                    alt="Filled star"
                                                    class="star-icon star-not-allowed"
                                                />
                                                <img
                                                    v-else
                                                    src="@/assets/images/icons/star.svg"
                                                    alt="Empty star"
                                                    class="star-icon star-not-allowed"
                                                />
                                            </div>
                                        </template>
                                        <template
                                            #tooltip-content
                                            v-if="getStarTooltip(record)"
                                        >
                                            {{ getStarTooltip(record) }}
                                        </template>
                                    </ToolTip>
                                    <template v-else>
                                        <img
                                            v-if="record[starredField.key]"
                                            src="@/assets/images/icons/filled-star.svg"
                                            alt="Filled star"
                                            class="star-icon star-clickable"
                                            @click="$emit('star-click', record)"
                                        />
                                        <img
                                            v-else
                                            src="@/assets/images/icons/star.svg"
                                            alt="Empty star"
                                            class="star-icon star-clickable"
                                            @click="$emit('star-click', record)"
                                        />
                                    </template>
                                </span>
                                <!-- User Avatar -->
                                <span
                                    v-if="
                                        fields.find(
                                            (field) =>
                                                field.type === 'user-avatar'
                                        )
                                    "
                                >
                                    <UserAvatar
                                        :key="record.profile_image_id"
                                        :user="record"
                                        class="avatar-style"
                                        size="2.25rem"
                                        :clickable="
                                            isFieldClickable(
                                                fields.find(
                                                    (field) =>
                                                        field.type ===
                                                        'user-avatar'
                                                ),
                                                record
                                            )
                                        "
                                    />
                                </span>
                                <!-- Name - can be a user-avatar or a name type -->
                                <span
                                    class="mobile-name"
                                    v-if="nameFields"
                                    :class="{
                                        'clickable-content': isFieldClickable(
                                            nameFields,
                                            record
                                        ),
                                    }"
                                    @click="
                                        clickableContentClick(
                                            nameFields,
                                            record
                                        )
                                    "
                                >
                                    {{ record?.name }}
                                </span>
                                <!-- Show Status Icon if available -->
                                <span
                                    v-if="
                                        fields.find(
                                            (field) =>
                                                field.type === 'icon' &&
                                                field.key === 'status'
                                        )
                                    "
                                    class="icon-style"
                                >
                                    <ToolTip>
                                        <template #tooltip-trigger>
                                            <i
                                                class="fi"
                                                :class="
                                                    record['status']?.class ||
                                                    record['status']
                                                "
                                            ></i>
                                        </template>
                                        <template
                                            #tooltip-content
                                            v-if="
                                                record['status']?.tooltipContent
                                            "
                                        >
                                            {{
                                                record['status']?.tooltipContent
                                            }}
                                        </template>
                                    </ToolTip>
                                </span>
                            </template>
                        </div>
                        <table class="w-100 mobile-table">
                            <tbody>
                                <tr
                                    v-for="field in mobileFields"
                                    :key="field.key"
                                    class="table-row"
                                >
                                    <!-- First column: Label -->
                                    <td
                                        class="cell mobile-label"
                                        :data-label="field.label"
                                    >
                                        {{ field.label }}
                                    </td>
                                    <!-- Second column: Value -->
                                    <td class="cell">
                                        <slot
                                            :name="`cell-${field.key}`"
                                            :field="field"
                                            :record="record"
                                        >
                                            <span
                                                v-if="field.type === 'icon'"
                                                class="icon-style"
                                            >
                                                <ToolTip>
                                                    <template #tooltip-trigger>
                                                        <i
                                                            class="fi"
                                                            :class="
                                                                record[
                                                                    field.key
                                                                ]?.class ||
                                                                record[
                                                                    field.key
                                                                ]
                                                            "
                                                        ></i>
                                                    </template>
                                                    <template
                                                        #tooltip-content
                                                        v-if="
                                                            record[field.key]
                                                                ?.tooltipContent
                                                        "
                                                    >
                                                        {{
                                                            record[field.key]
                                                                ?.tooltipContent
                                                        }}
                                                    </template>
                                                </ToolTip>
                                            </span>
                                            <span
                                                v-else-if="
                                                    field.type === 'boolean'
                                                "
                                            >
                                                <i
                                                    v-if="record[field.key]"
                                                    class="fi fi-check"
                                                ></i>
                                                <i v-else class="fi fi-x"></i>
                                            </span>
                                            <router-link
                                                v-else-if="
                                                    field.type === 'link'
                                                "
                                                :to="record[field.key]"
                                            >
                                                {{ record[field.key] }}
                                            </router-link>
                                            <a
                                                v-else-if="
                                                    field.type ===
                                                    'external-link'
                                                "
                                                :href="record[field.key]"
                                                target="_blank"
                                            >
                                                {{ record[field.key] }}
                                            </a>
                                            <span
                                                v-else-if="
                                                    field.type === 'starred'
                                                "
                                            >
                                                <ToolTip
                                                    v-if="
                                                        isStarDisabled(record)
                                                    "
                                                >
                                                    <template #tooltip-trigger>
                                                        <div
                                                            class="star-disabled"
                                                        >
                                                            <img
                                                                v-if="
                                                                    record[
                                                                        field
                                                                            .key
                                                                    ]
                                                                "
                                                                src="@/assets/images/icons/filled-star.svg"
                                                                alt="Filled star"
                                                                class="star-icon star-not-allowed"
                                                            />
                                                            <img
                                                                v-else
                                                                src="@/assets/images/icons/star.svg"
                                                                alt="Empty star"
                                                                class="star-icon star-not-allowed"
                                                            />
                                                        </div>
                                                    </template>
                                                    <template
                                                        #tooltip-content
                                                        v-if="
                                                            getStarTooltip(
                                                                record
                                                            )
                                                        "
                                                    >
                                                        {{
                                                            getStarTooltip(
                                                                record
                                                            )
                                                        }}
                                                    </template>
                                                </ToolTip>
                                                <img
                                                    v-else-if="
                                                        record[field.key]
                                                    "
                                                    src="@/assets/images/icons/filled-star.svg"
                                                    alt="Filled star"
                                                    class="star-icon star-clickable"
                                                    @click="
                                                        $emit(
                                                            'star-click',
                                                            record
                                                        )
                                                    "
                                                />
                                                <img
                                                    v-else
                                                    src="@/assets/images/icons/star.svg"
                                                    alt="Empty star"
                                                    class="star-icon star-clickable"
                                                    @click="
                                                        $emit(
                                                            'star-click',
                                                            record
                                                        )
                                                    "
                                                />
                                            </span>
                                            <span
                                                v-else-if="
                                                    field.type === 'date'
                                                "
                                            >
                                                {{
                                                    formatDate(
                                                        record[field.key]
                                                    )
                                                }}
                                            </span>
                                            <span
                                                v-else-if="
                                                    field.type === 'percentage'
                                                "
                                            >
                                                {{
                                                    typeof record[field.key] ===
                                                        'number' &&
                                                    !isNaN(record[field.key])
                                                        ? record[field.key] +
                                                          '%'
                                                        : '—'
                                                }}
                                            </span>
                                            <span
                                                v-else
                                                :class="{
                                                    'clickable-content':
                                                        isFieldClickable(
                                                            field,
                                                            record
                                                        ),
                                                }"
                                                @click="
                                                    clickableContentClick(
                                                        field,
                                                        record
                                                    )
                                                "
                                            >
                                                {{
                                                    record[field.key] ||
                                                    fallbackText
                                                }}
                                            </span>
                                        </slot>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <!-- Render other buttons -->
                        <div
                            v-if="reservedSpace > 0"
                            class="actions-wrapper"
                            :data-label="'Actions'"
                        >
                            <template
                                v-for="button in otherButtonsFiltered(record)"
                                :key="button.label || button.icon"
                            >
                                <IconButton
                                    v-if="!button.label"
                                    :preset="getButtonPreset(button)"
                                    :class="button.class"
                                    :icon-class="
                                        button.icon && !getButtonPreset(button)
                                            ? button.icon
                                            : ''
                                    "
                                    :aria-label="
                                        getOtherButtonAriaLabel(button, record)
                                    "
                                    :tooltip="
                                        getOtherButtonTooltip(button, record) ||
                                        button.tooltip
                                    "
                                    :tooltip-props="button.tooltipProps || {}"
                                    :disabled="
                                        isOtherButtonDisabled(button, record)
                                    "
                                    @click="otherButtonClick(button, record)"
                                />
                                <ToolTip v-else>
                                    <template #tooltip-trigger>
                                        <button
                                            :class="[
                                                'icon-btn',
                                                button.class ||
                                                    'icon-btn-white',
                                            ]"
                                            @click="
                                                otherButtonClick(button, record)
                                            "
                                            :disabled="
                                                isOtherButtonDisabled(
                                                    button,
                                                    record
                                                )
                                            "
                                        >
                                            <i
                                                v-if="button.icon"
                                                :class="['fi', button.icon]"
                                            ></i>
                                            <span>{{ button.label }}</span>
                                        </button>
                                    </template>
                                    <template
                                        #tooltip-content
                                        v-if="
                                            getOtherButtonTooltip(
                                                button,
                                                record
                                            )
                                        "
                                    >
                                        {{
                                            getOtherButtonTooltip(
                                                button,
                                                record
                                            )
                                        }}
                                    </template>
                                </ToolTip>
                            </template>

                            <IconButton
                                v-if="
                                    typeof hasDelete === 'function'
                                        ? hasDelete(record)
                                        : hasDelete
                                "
                                preset="delete"
                                :tooltip="
                                    getDeleteButtonTooltip(record) || true
                                "
                                :disabled="isDeleteButtonDisabled(record)"
                                @click="openModal(record)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Desktop View -->
        <div class="desktop-table-wrapper">
            <table :class="desktopTableClass" ref="tableRef">
                <thead class="w-100">
                    <tr class="table-header">
                        <th
                            v-for="field in fields"
                            :key="field.key"
                            class="cell"
                            :style="{
                                width: calculateColumnWidth(field.width),
                            }"
                            @click="
                                sortable && field.sortable
                                    ? sort(field.key)
                                    : null
                            "
                            :class="{
                                sortable: sortable && field.sortable,
                                'text-center': field.type === 'icon',
                            }"
                            :data-label="field.label"
                        >
                            <div class="d-flex align-items-center gap-1">
                                <span
                                    class="header-text"
                                    :title="
                                        field.key === 'is_starred'
                                            ? ''
                                            : field.label
                                    "
                                >
                                    {{
                                        field.key === 'is_starred'
                                            ? ''
                                            : field.label
                                    }}
                                </span>

                                <div
                                    v-if="sortable && field.sortable"
                                    class="d-inline-flex"
                                >
                                    <i v-if="!sortKey" class="fi fi-sort"></i>
                                    <i
                                        v-else-if="
                                            sortKey === field.key &&
                                            sortOrder === -1
                                        "
                                        class="fi fi-chevron-up"
                                    ></i>
                                    <i
                                        v-else-if="
                                            sortKey === field.key &&
                                            sortOrder === 1
                                        "
                                        class="fi fi-chevron-down"
                                    ></i>
                                </div>
                            </div>
                        </th>
                        <th
                            v-if="reservedSpace > 0"
                            class="actions-col"
                            :style="{ width: reservedSpace + 'px' }"
                            :data-label="'Actions'"
                        ></th>
                    </tr>
                </thead>
                <tbody class="w-100">
                    <tr
                        v-for="record in records"
                        class="table-row"
                        :key="record[idField || 'id']"
                        :style="record?.rowStyle"
                    >
                        <td
                            class="cell"
                            v-for="field in fields"
                            :key="field.key"
                            :data-label="field.label"
                            :style="{
                                width: calculateColumnWidth(field.width),
                            }"
                        >
                            <slot
                                :name="`cell-${field.key}`"
                                :field="field"
                                :record="record"
                            >
                                <span
                                    v-if="field.type === 'icon'"
                                    class="icon-style"
                                >
                                    <ToolTip>
                                        <template #tooltip-trigger>
                                            <i
                                                class="fi"
                                                :class="
                                                    record[field.key]?.class ||
                                                    record[field.key]
                                                "
                                            ></i>
                                        </template>
                                        <template
                                            #tooltip-content
                                            v-if="
                                                record[field.key]
                                                    ?.tooltipContent
                                            "
                                        >
                                            {{
                                                record[field.key]
                                                    ?.tooltipContent
                                            }}
                                        </template>
                                    </ToolTip>
                                </span>
                                <span
                                    v-else-if="field.type === 'starred'"
                                    style="text-align: center"
                                >
                                    <template v-if="record?.company_id != 0">
                                        <ToolTip v-if="isStarDisabled(record)">
                                            <template #tooltip-trigger>
                                                <div class="star-disabled">
                                                    <img
                                                        v-if="record[field.key]"
                                                        src="@/assets/images/icons/filled-star.svg"
                                                        alt="Filled star"
                                                        class="star-icon star-not-allowed"
                                                    />
                                                    <img
                                                        v-else
                                                        src="@/assets/images/icons/star.svg"
                                                        alt="Empty star"
                                                        class="star-icon star-not-allowed"
                                                    />
                                                </div>
                                            </template>
                                            <template
                                                #tooltip-content
                                                v-if="getStarTooltip(record)"
                                            >
                                                {{ getStarTooltip(record) }}
                                            </template>
                                        </ToolTip>
                                        <template v-else>
                                            <img
                                                v-if="record[field.key]"
                                                src="@/assets/images/icons/filled-star.svg"
                                                alt="Filled star"
                                                class="star-icon star-clickable"
                                                @click="
                                                    $emit('star-click', record)
                                                "
                                            />
                                            <img
                                                v-else
                                                src="@/assets/images/icons/star.svg"
                                                alt="Empty star"
                                                class="star-icon star-clickable"
                                                @click="
                                                    $emit('star-click', record)
                                                "
                                            />
                                        </template>
                                    </template>
                                </span>
                                <span v-else-if="field.type === 'boolean'">
                                    <i
                                        v-if="record[field.key]"
                                        class="fi fi-check"
                                    ></i>
                                    <i v-else class="fi fi-x"></i>
                                </span>
                                <router-link
                                    v-else-if="field.type === 'link'"
                                    :to="record[field.key]"
                                >
                                    {{ record[field.key] }}
                                </router-link>
                                <a
                                    v-else-if="field.type === 'external-link'"
                                    :href="record[field.key]"
                                    target="_blank"
                                >
                                    {{ record[field.key] }}
                                </a>
                                <span v-else-if="field.type === 'date'">
                                    {{ formatDate(record[field.key]) }}
                                </span>
                                <span
                                    v-else-if="
                                        field.type === 'name' ||
                                        field.type === 'user-avatar'
                                    "
                                    class="d-flex align-items-center"
                                    style="gap: var(--space-2)"
                                >
                                    <UserAvatar
                                        v-if="field.type === 'user-avatar'"
                                        :key="record.profile_image_id"
                                        :user="record"
                                        class="avatar-style"
                                        size="1.75rem"
                                        :clickable="
                                            isFieldClickable(field, record)
                                        "
                                    />
                                    <span
                                        :class="{
                                            'clickable-content':
                                                isFieldClickable(field, record),
                                        }"
                                        @click="
                                            clickableContentClick(field, record)
                                        "
                                    >
                                        {{ record?.name }}
                                    </span>
                                </span>
                                <span v-else-if="field.type === 'percentage'">
                                    {{
                                        typeof record[field.key] === 'number' &&
                                        !isNaN(record[field.key])
                                            ? record[field.key] + '%'
                                            : '—'
                                    }}
                                </span>
                                <span
                                    v-else
                                    :class="{
                                        'clickable-content': isFieldClickable(
                                            field,
                                            record
                                        ),
                                    }"
                                    @click="
                                        clickableContentClick(field, record)
                                    "
                                    >{{
                                        record[field.key] || fallbackText
                                    }}</span
                                >
                            </slot>
                        </td>
                        <td
                            v-if="reservedSpace > 0"
                            :data-label="'Actions'"
                            class="actions-col"
                            :style="{ width: reservedSpace + 'px' }"
                        >
                            <div class="actions-wrapper">
                                <template
                                    v-for="button in otherButtonsFiltered(
                                        record
                                    )"
                                    :key="button.label || button.icon"
                                >
                                    <IconButton
                                        v-if="!button.label"
                                        :preset="getButtonPreset(button)"
                                        :class="button.class"
                                        :icon-class="
                                            button.icon &&
                                            !getButtonPreset(button)
                                                ? button.icon
                                                : ''
                                        "
                                        :aria-label="
                                            getOtherButtonAriaLabel(
                                                button,
                                                record
                                            )
                                        "
                                        :tooltip="
                                            getOtherButtonTooltip(
                                                button,
                                                record
                                            ) || button.tooltip
                                        "
                                        :tooltip-props="
                                            button.tooltipProps || {}
                                        "
                                        :disabled="
                                            isOtherButtonDisabled(
                                                button,
                                                record
                                            )
                                        "
                                        @click="
                                            otherButtonClick(button, record)
                                        "
                                    />
                                    <ToolTip v-else>
                                        <template #tooltip-trigger>
                                            <button
                                                :class="[
                                                    'icon-btn',
                                                    button.class ||
                                                        'icon-btn-white',
                                                ]"
                                                @click="
                                                    otherButtonClick(
                                                        button,
                                                        record
                                                    )
                                                "
                                                :disabled="
                                                    isOtherButtonDisabled(
                                                        button,
                                                        record
                                                    )
                                                "
                                            >
                                                <i
                                                    v-if="button.icon"
                                                    :class="['fi', button.icon]"
                                                ></i>
                                                <span>{{ button.label }}</span>
                                            </button>
                                        </template>
                                        <template
                                            #tooltip-content
                                            v-if="
                                                getOtherButtonTooltip(
                                                    button,
                                                    record
                                                )
                                            "
                                        >
                                            {{
                                                getOtherButtonTooltip(
                                                    button,
                                                    record
                                                )
                                            }}
                                        </template>
                                    </ToolTip>
                                </template>

                                <IconButton
                                    v-if="
                                        typeof hasDelete === 'function'
                                            ? hasDelete(record)
                                            : hasDelete
                                    "
                                    preset="delete"
                                    :tooltip="
                                        getDeleteButtonTooltip(record) || true
                                    "
                                    :disabled="isDeleteButtonDisabled(record)"
                                    @click="openModal(record)"
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <ConfirmModal
            :visible="isModalVisible"
            :title="resolvedDeleteTitle"
            :confirm-button-text="resolvedDeleteConfirmButtonText"
            @close="closeModal"
            @confirm="deleteRecord"
        >
            {{ resolvedDeleteDescription }}
        </ConfirmModal>
    </div>
</template>

<script>
import moment from 'moment';
import UserAvatar from '@/components/UserAvatar.vue';
import ToolTip from '@/components/fields/ToolTip.vue';
import IconButton from '@/components/IconButton.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';

export default {
    name: 'TableList',
    components: {
        UserAvatar,
        ToolTip,
        IconButton,
        ConfirmModal,
    },
    props: {
        records: {
            type: Array,
            required: true,
        },
        /**
         * Defines table columns. Each object in the array represents a column.
         * Example field object:
         * {
         *   key: 'status', // Corresponds to a key in a record object.
         *   label: 'Status', // Column header text.
         *   type: 'icon', // Determines rendering. Other types: 'boolean', 'link', 'date', 'name', 'user-avatar', 'percentage', 'starred'
         *   sortable: true, // Enables sorting on this column.
         *   width: '20%', // Column width.
         *   clickable: true, // If the cell content is clickable
         *   click: 'item-clicked' // event to be emitted
         * }
         *
         * For fields with `type: 'icon'`, the record's value for that key can be a string
         * (the icon class) or an object for more complex cases:
         * {
         *   class: 'fi-check-circle',      // The CSS class for the icon.
         *   tooltipContent: 'Task is complete' // Optional tooltip text.
         * }
         */
        fields: {
            type: Array,
            required: true,
        },
        idField: {
            type: String,
            default: 'id',
        },
        otherButtons: {
            // [ { label, icon, class?, click, skipIconPreset?, disabledState?, condition?, tooltip? } ]
            // When skipIconPreset is true, IconButton does not use the icon→preset map (e.g. keep fi-add
            // as raw icon-class so class: 'icon-btn-white' is not combined with preset add's primary variant).
            type: Array,
            required: false,
        },
        // this function is used to determine if the record has a delete button. if the function returns true, the delete button will be shown. if the function returns false, the delete button will not be shown.
        hasDelete: {
            type: [Function, Boolean],
            default: false,
        },
        deleteDisabledState: {
            type: [Function, Boolean],
            default: false,
        },
        starDisabledState: {
            type: [Function, Boolean],
            default: false,
        },
        sortable: {
            type: Boolean,
            default: false,
        },
        totalWidth: {
            type: Number,
            default: null,
        },
        minColumnWidth: {
            type: Number,
            default: 40, // Minimum column width in pixels
        },
        enforcePercentageLimits: {
            type: Boolean,
            default: true, // Whether to scale percentages if they exceed 100%
        },
        showHeaderMobile: {
            type: Boolean,
            default: true, // Whether to show the header on mobile
        },
        recordType: {
            type: String,
            default: 'record',
        },
        forceMobileView: {
            type: Boolean,
            default: false,
        },
        deleteDescription: {
            type: String,
            default: '',
        },
        deleteTitle: {
            type: String,
            default: '',
        },
        deleteConfirmButtonText: {
            type: String,
            default: '',
        },
        forceDesktopView: {
            type: Boolean,
            default: false,
        },
        fallbackText: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            sortKey: null,
            sortOrder: 1, // 1 for ascending, -1 for descending
            isModalVisible: false,
            currentRecord: null,
            tableWidth: 0,
            resizeTimer: null,
        };
    },
    emits: [
        'sort',
        'button-click',
        'delete',
        'star-click',
        'edit',
        'view',
        'download',
        'chat',
        'items',
        'updateQuantity',
    ],
    computed: {
        mobileDisplayClass() {
            if (this.forceMobileView) return 'd-block';
            if (this.forceDesktopView) return 'd-none';
            return 'd-block d-lg-none';
        },
        desktopTableClass() {
            if (this.forceMobileView) return 'd-none';
            if (this.forceDesktopView) {
                return 'table table-borderless table-hover desktop-tabl';
            }
            return 'd-lg-table d-none table table-borderless table-hover desktop-tabl';
        },
        // Computed property that returns the field configuration for displaying a user's name or avatar in mobile veiw
        nameFields() {
            return this.fields.find(
                (field) => field.type === 'name' || field.type === 'user-avatar'
            );
        },

        starredField() {
            return this.fields.find((field) => field.type === 'starred');
        },

        availableWidth() {
            return this.tableWidth - this.reservedSpace;
        },
        mobileHeaderFields() {
            // Get all fields marked with mobileHeader: true, in order
            return this.fields.filter((field) => field.mobileHeader === true);
        },
        mobileFields() {
            // Start with all fields
            let filteredFields = [...this.fields];

            if (!this.showHeaderMobile) return filteredFields;

            // If showHeaderMobile is true, filter out name fields, icon fields which are status fields, and user-avatar fields
            // Also filter out fields marked with mobileHeader: true (they appear in header instead)
            if (this.showHeaderMobile) {
                filteredFields = filteredFields.filter((field) => {
                    // Filter out default header fields
                    if (field.key.toLowerCase() === 'name') return false;
                    if (field.type === 'icon' && field.key === 'status')
                        return false;
                    if (field.type === 'user-avatar') return false;
                    if (field.type === 'starred') return false;

                    // Filter out fields marked for mobile header (they appear in header instead)
                    if (field.mobileHeader === true) return false;

                    // Allow parent components to mark fields to hide in mobile view
                    if (field.hideInMobile) return false;

                    return true;
                });
            }

            // Check if there's a starred field
            const starredIndex = filteredFields.findIndex(
                (field) =>
                    field.key === 'is_starred' || field.type === 'starred'
            );

            // If starred field exists, move it to the end
            if (starredIndex !== -1) {
                const starredField = filteredFields.splice(starredIndex, 1)[0];
                filteredFields.push(starredField);
            }

            return filteredFields;
        },
        reservedSpace() {
            if (
                !this.otherButtons ||
                (this.otherButtons.length === 0 && !this.hasDelete)
            )
                return 0;

            // Calculate space for buttons (34px per button) + padding (8px gaps)
            const buttonWidth = 34;
            const buttonGap = 8;
            const buttonCount =
                this.otherButtons.length + (this.hasDelete ? 1 : 0);

            return (
                buttonWidth * buttonCount + (buttonCount - 1) * buttonGap + 24
            ); // 24px for padding
        },
        totalPercentage() {
            // Calculate the total percentage used by all columns
            return this.fields.reduce((total, field) => {
                if (
                    typeof field.width === 'string' &&
                    field.width.includes('%')
                ) {
                    const percentValue = parseFloat(field.width);
                    return isNaN(percentValue) ? total : total + percentValue;
                }
                return total;
            }, 0);
        },
        scaleFactor() {
            // Calculate scale factor if total percentage exceeds or is less than 100%
            if (!this.enforcePercentageLimits) return 1;

            if (this.totalPercentage === 0) return 1;

            return 100 / this.totalPercentage;
        },
        resolvedDeleteTitle() {
            return (
                this.deleteTitle ||
                this.$t('common.deleteModal.title', {
                    recordType: this.recordType,
                })
            );
        },
        resolvedDeleteConfirmButtonText() {
            return (
                this.deleteConfirmButtonText ||
                this.$t('common.deleteModal.confirmButton', {
                    recordType: this.recordType,
                })
            );
        },
        resolvedDeleteDescription() {
            return (
                this.deleteDescription ||
                this.$t('common.deleteModal.description', {
                    recordType: this.recordType,
                })
            );
        },
    },
    watch: {
        tableWidth() {
            this.$forceUpdate();
        },
        fields: {
            handler() {
                this.$forceUpdate();
                this.$nextTick(this.updateTableWidth);
            },
            deep: true,
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.updateTableWidth();
            window.addEventListener('resize', this.handleResize);
        });
    },
    updated() {
        // Check if table width needs to be updated after component updates
        this.$nextTick(() => {
            this.updateTableWidth();
        });
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            // Use debounce to prevent too many updates
            clearTimeout(this.resizeTimer);
            this.resizeTimer = setTimeout(() => {
                this.updateTableWidth();
            }, 100);
        },
        updateTableWidth() {
            if (this.$refs.tableRef) {
                const newWidth =
                    this.totalWidth || this.$refs.tableRef.clientWidth;
                if (newWidth !== this.tableWidth) {
                    this.tableWidth = newWidth;
                    // Manually trigger a resize event on window to ensure all components update
                    window.dispatchEvent(new Event('resize'));
                }
            }
        },
        calculateColumnWidth(width) {
            // If width is not provided, return 'auto'
            if (!width) return 'auto';

            // If width is a percentage
            if (typeof width === 'string' && width.includes('%')) {
                const percentValue = parseFloat(width);
                if (isNaN(percentValue)) {
                    return 'auto';
                }

                // Apply scale factor to make columns fill available space
                const adjustedPercentValue = percentValue * this.scaleFactor;

                // Calculate pixel width if table width is available
                if (this.availableWidth > 0) {
                    // Apply a minimum width to prevent columns from becoming too narrow
                    const calculatedWidth = Math.max(
                        this.minColumnWidth,
                        Math.floor(
                            (this.availableWidth * adjustedPercentValue) / 100
                        )
                    );
                    return `${calculatedWidth}px`;
                }

                // Fallback to percentage
                return `${adjustedPercentValue}%`;
            }

            // For absolute pixel widths, return as is
            return width;
        },
        otherButtonClick(btn, record) {
            this.$emit(btn.click, record);
        },
        // Toggles sorting or changes sorting column
        sort(key) {
            if (this.sortKey === key) {
                // Cycle through the states: ascending -> descending -> no sort
                if (this.sortOrder === 1) {
                    this.sortOrder = -1; // Descending
                } else if (this.sortOrder === -1) {
                    this.sortOrder = 0; // No sort
                    this.sortKey = null; // Clear the sort key
                } else {
                    this.sortOrder = 1; // Ascending
                }
            } else {
                // If it's a new key, start with ascending
                this.sortKey = key;
                this.sortOrder = 1;
            }
            this.$emit('sort', this.sortKey, this.sortOrder);
        },
        openModal(record) {
            this.isModalVisible = true;
            this.currentRecord = record;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        deleteRecord() {
            this.$emit('delete', this.currentRecord);
            this.isModalVisible = false;
        },
        // Public method that can be called to refresh the table
        refresh() {
            this.$nextTick(() => {
                this.updateTableWidth();
            });
        },
        formatDate(dateString) {
            if (!dateString) return '';

            return moment.utc(dateString).format('MM/DD/YYYY');
        },
        // Debug method to help troubleshoot column width issues
        getColumnWidthInfo() {
            const info = {
                tableWidth: this.tableWidth,
                availableWidth: this.availableWidth,
                totalPercentage: this.totalPercentage,
                scaleFactor: this.scaleFactor,
                columns: this.fields.map((field) => ({
                    key: field.key,
                    width: field.width,
                    calculatedWidth: this.calculateColumnWidth(field.width),
                })),
            };
            return info;
        },

        otherButtonsFiltered(record) {
            return this.otherButtons.filter(
                // if condition is not provided, show the button. if condition is provided, show the button if the condition is true, otherwise hide the button
                (button) => !button.condition || button.condition(record)
            );
        },

        /**
         * Map a button's icon class to an IconButton preset name.
         * Recognises the three standard action icons: view, edit, delete.
         * Returns null for any other icon so IconButton falls back to iconClass.
         */
        getButtonPreset(button) {
            if (button.skipIconPreset) {
                return null;
            }
            const iconMap = {
                'fi-eye': 'view',
                'fi-edit': 'edit',
                'fi-trash': 'delete',
                'fi-add': 'add',
                'fi-info': 'info',
                'fi-bell': 'bell',
                'fi-chat': 'chat',
                'fi-thread': 'thread',
                'fi-check': 'check',
                'fi-copy': 'copy',
                'fi-download': 'download',
                'fi-filter': 'filter',
            };
            return iconMap[button.icon] || null;
        },

        isFieldClickable(field, record) {
            if (field?.clickable) {
                if (typeof field.clickable === 'function') {
                    return field.clickable(record);
                }
                return field.clickable;
            }
            return false;
        },

        clickableContentClick(field, record) {
            if (this.isFieldClickable(field, record) && field?.click) {
                this.$emit(field.click, record);
            }
        },

        getOtherButtonsDisabledState(button, record) {
            if (
                button.disabledState &&
                typeof button.disabledState === 'function'
            ) {
                return button.disabledState(record);
            }
            return { disabled: false, tooltip: '' };
        },

        isOtherButtonDisabled(button, record) {
            return (
                this.getOtherButtonsDisabledState(button, record)?.disabled ||
                false
            );
        },

        getOtherButtonAriaLabel(button, record) {
            if (typeof button.ariaLabel === 'function') {
                return button.ariaLabel(record);
            }
            return button.ariaLabel || button.label || '';
        },

        getOtherButtonTooltip(button, record) {
            const disabledTooltip = this.getOtherButtonsDisabledState(
                button,
                record
            )?.tooltip;
            if (disabledTooltip) return disabledTooltip;
            if (typeof button.tooltip === 'function') {
                return button.tooltip(record);
            }
            return button.tooltip || '';
        },

        getDeleteButtonDisabledState(record) {
            if (
                this.deleteDisabledState &&
                typeof this.deleteDisabledState === 'function'
            ) {
                return this.deleteDisabledState(record);
            }
            return { disabled: false, tooltip: '' };
        },

        isDeleteButtonDisabled(record) {
            return this.getDeleteButtonDisabledState(record)?.disabled || false;
        },

        getDeleteButtonTooltip(record) {
            return this.getDeleteButtonDisabledState(record)?.tooltip || '';
        },

        getStarDisabledState(record) {
            if (
                this.starDisabledState &&
                typeof this.starDisabledState === 'function'
            ) {
                return this.starDisabledState(record);
            }
            return { disabled: false, tooltip: '' };
        },

        isStarDisabled(record) {
            return this.getStarDisabledState(record)?.disabled || false;
        },

        getStarTooltip(record) {
            return this.getStarDisabledState(record)?.tooltip || '';
        },
    },
};
</script>

<style scoped>
.table {
    border-collapse: collapse;
    overflow-x: auto;
    max-width: 100%;
    font-size: 0.875rem;
    width: 100%;
    /* Allow the table to adjust its layout based on content */
    table-layout: auto;
    margin-bottom: var(--space-3);
}

.cell {
    font-size: 0.875rem;
    line-height: 1.29;
    color: var(--black);
}

th {
    background-color: var(--primary-25) !important;
    padding: var(--space-2) 0.9375rem;
    font-weight: 700;
    vertical-align: middle;
    user-select: none;
    white-space: nowrap;
}

thead th:first-child {
    border-top-left-radius: var(--radius-sm);
    padding-left: var(--space-4);
    /* Top-left corner */
}

thead th:last-child {
    border-top-right-radius: var(--radius-sm);
    padding-right: var(--space-4);
    /* Top-right corner */
}

/* Clamp header text to 2 lines */
.header-text {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
    font-size: 0.875rem;
}

.desktop-table-wrapper {
    overflow-x: auto;
    overflow-y: visible;
    width: 100%;
    max-width: 100%;

    /* Scrollbar behavior matching SidePanel */
    &::-webkit-scrollbar {
        height: 3px;
        width: 3px;
        opacity: 0;
        transition: all 0.3s ease;
    }

    &:hover::-webkit-scrollbar {
        opacity: 1;
    }

    &::-webkit-scrollbar-track {
        background: var(--neutral-450);
        border-radius: var(--radius-sm);
    }

    &::-webkit-scrollbar-thumb {
        background: var(--neutral-600);
        border-radius: var(--radius-sm);
    }

    &::-webkit-scrollbar-thumb:hover {
        background: var(--neutral-700);
    }

    /* Firefox scrollbar styling */
    scrollbar-width: thin;
    scrollbar-color: var(--neutral-600) var(--neutral-450);
}

.desktop-table {
    display: table;
    width: 100%;
    min-width: auto !important;
    /* Add responsive container */
    overflow-x: auto;
}

td {
    padding: 0.8125rem var(--space-3);
    cursor: default;
    word-break: break-word; /* breaks long words at arbitrary points */
    overflow-wrap: anywhere; /* modern, allows breaks anywhere */
    hyphens: auto;
}

tr:nth-child(even) {
    background-color: var(--neutral-400);
}

tr:nth-child(odd) {
    background-color: var(--white);
}

th.sortable {
    cursor: pointer;
}

th div {
    font-size: 1rem;
    color: var(--neutral-900);
    min-width: 16px;
    margin-left: 0.25rem;
}

.table-row {
    height: auto;
    max-height: 4.5rem;
    line-height: 1.5;
}

.table-row td {
    height: 2.75rem;
    padding: 0.5rem;
    max-height: 4.5rem;
    vertical-align: middle;
    min-height: 2.75rem;
}

.table-header th {
    min-height: 2.125rem;
}

/* Apply truncation only to text content — not inline pills (StatusBadge sets data-table-inline-pill). */
.table-row td span:not(.fi):not([class^='icon']):not([data-table-inline-pill]) {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
}

.table-row td span[data-table-inline-pill] {
    display: inline-flex;
    flex-shrink: 0;
    white-space: nowrap;
    word-break: normal;
    overflow-wrap: normal;
}

/* Force mobile view styles when .force-mobile class is present */
.force-mobile thead {
    display: none;
}
.force-mobile tr {
    border: 1px solid var(--neutral-600);
    border-radius: var(--radius-sm);
    border-collapse: collapse;
    padding: var(--space-1);
}
.force-mobile td {
    border: 1px solid var(--neutral-600);
    border-collapse: collapse;
    padding: var(--space-1) var(--space-2);
    height: auto;
}
.force-mobile td:first-child {
    background-color: var(--primary-25);
    width: 30%;
    min-width: 100px;
    text-align: left;
    white-space: normal !important;
    overflow-wrap: normal !important;
    word-break: normal !important;
}
.force-mobile td:last-child {
    width: 70%;
    max-width: 0;
    overflow: hidden;
    padding: var(--space-1) var(--space-2) !important;
    text-align: right;
}
.force-mobile
    td:last-child
    span:not(.fi):not([class^='icon']):not([data-table-inline-pill]),
.force-mobile td:last-child a {
    display: block;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
}
.force-mobile td:last-child .icon-style {
    width: 100%;
    justify-content: flex-end;
}
.force-mobile .mobile-card {
    border-radius: var(--radius-lg);
    padding: var(--space-3);
    background-color: var(--neutral-400);
    min-width: fit-content;
    margin-bottom: var(--space-4);
}
.force-mobile .mobile-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
}
.force-mobile .mobile-name {
    flex: 1;
    min-width: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
    font-size: 1.125rem;
    line-height: var(--space-6);
    font-weight: 600;
    word-break: break-word; /* breaks long words at arbitrary points */
    overflow-wrap: anywhere; /* modern, allows breaks anywhere */
    hyphens: auto;
}
.force-mobile .mobile-status {
    font-size: 1.5rem;
}
.force-mobile .actions-wrapper {
    justify-content: flex-start;
}
.force-mobile .mobile-label {
    font-weight: 700;
}
.force-mobile .mobile-table {
    margin-bottom: var(--space-4);
    table-layout: fixed;
    border-collapse: collapse;
}
.force-mobile .mobile-table-container {
    margin-bottom: calc(-1 * var(--space-4));
}
.force-mobile.table-responsive-sm {
    overflow: hidden !important;
    overflow-x: hidden !important;
    overflow-y: hidden !important;
}

.force-desktop thead {
    display: table-header-group !important;
}

.force-desktop tr {
    border: none !important;
    border-radius: 0 !important;
    padding: 0 !important;
}

.force-desktop td {
    border: none !important;
    padding: 0.8125rem var(--space-3) !important;
    height: auto !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    hyphens: auto;
}

.force-desktop .table-row td {
    height: 2.75rem !important;
    padding: 0.5rem !important;
    max-height: 4.5rem;
    vertical-align: middle;
    min-height: 2.75rem;
}

.force-desktop td:first-child {
    background-color: inherit !important;
    min-width: 0 !important;
    text-align: inherit !important;
    white-space: normal !important;
    overflow: visible !important;
    text-overflow: clip !important;
}

.force-desktop td:last-child {
    max-width: none !important;
    padding: 0.5rem !important;
    text-align: inherit !important;
}

.force-desktop
    td:last-child
    span:not(.fi):not([class^='icon']):not([data-table-inline-pill]),
.force-desktop td:last-child a {
    display: -webkit-box !important;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal !important;
    text-align: inherit !important;
}

.force-desktop td:last-child .icon-style {
    width: auto !important;
    justify-content: center !important;
}

.force-desktop .actions-wrapper {
    justify-content: flex-end !important;
}

.force-desktop.table-responsive-sm {
    overflow-x: auto !important;
    overflow-y: visible !important;
}

.table-responsive-sm {
    width: 100%;
}

.actions-wrapper {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--space-2);
}

.icon-style {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
}

.icon-style i {
    font-size: 1.125rem;
}

.user-avatar {
    width: 2.5rem;
    height: 2.5rem;
}

.clickable-content {
    color: var(--primary-400);
    font-weight: 600;
    cursor: pointer;
}

.clickable-content:hover {
    text-decoration: underline;
    text-decoration-skip-ink: none;
    text-underline-position: from-font;
    color: var(--black);
}

/* Sticky actions column for desktop view */
.actions-col {
    position: sticky;
    right: 0;
    z-index: 3;
    background-color: inherit;
}

.star-disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.star-icon {
    display: inline-block;
}

.star-not-allowed {
    pointer-events: none;
}

.star-clickable {
    cursor: pointer;
}

@media (max-width: 991.98px) {
    thead {
        display: none;
        /* Hide table headers on small screens */
    }

    tr {
        border: 1px solid var(--neutral-600);
        border-radius: var(--radius-sm);
        border-collapse: collapse;
        padding: var(--space-1);
    }

    td {
        border: 1px solid var(--neutral-600);
        border-collapse: collapse;
        padding: var(--space-1) var(--space-2);
    }

    .table-row td {
        height: auto;
    }

    td:first-child {
        background-color: var(--primary-25);
        width: 30%;
        min-width: 100px;
        text-align: left;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    td:last-child {
        width: 70%;
        max-width: 0;
        padding: var(--space-1) var(--space-2) !important;
        text-align: right;
        /* overflow: hidden; */
    }

    td:last-child
        span:not(.fi):not([class^='icon']):not([data-table-inline-pill]),
    td:last-child a {
        display: block;
        width: 100%;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: right;
    }

    td:last-child .icon-style {
        width: 100%;
        justify-content: flex-end;
    }

    .mobile-card {
        border-radius: var(--radius-lg);
        padding: var(--space-3);
        background-color: var(--neutral-400);
        min-width: fit-content;
        margin-bottom: var(--space-4);
        overflow: visible !important;
        position: relative;
    }

    .mobile-header {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;
        gap: var(--space-3);
    }

    .mobile-name {
        flex: 1;
        min-width: 0;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        white-space: normal;
        font-size: 1.125rem;
        line-height: var(--space-6);
        font-weight: 600;
        word-break: break-word; /* breaks long words at arbitrary points */
        overflow-wrap: anywhere; /* modern, allows breaks anywhere */
        hyphens: auto;
    }

    .mobile-status {
        font-size: 1.5rem;
    }

    .actions-wrapper {
        justify-content: flex-end;
    }

    .mobile-label {
        font-weight: 700;
        white-space: normal !important;
        overflow-wrap: normal;
        word-break: normal !important;
    }

    .mobile-table {
        margin-bottom: var(--space-4);
        table-layout: fixed;
        border-collapse: collapse;
    }

    .mobile-table-container {
        /* hack to reduce the bottom margin of the last card */
        margin-bottom: calc(-1 * var(--space-4));
        overflow: visible !important;
    }

    .table-responsive-sm {
        overflow: visible !important;
    }
}
</style>
